export interface CutOptimizationRequest {
	dimension: string;
	cutsMm: number[];
	stockLengthsMm: number[];
}

export interface CutBoardPlan {
	cutsMm: number[];
	remainingMm: number;
}

export interface CutOptimizationByDimension {
	dimension: string;
	stockLengthMm: number;
	totalBoardsToBuy: number;
	wastePercentage: string;
	cutPlan: CutBoardPlan[];
}

function getCandidateStockLengths(
	cutsMm: number[],
	stockLengthsMm: number[],
): number[] {
	const maxCut = Math.max(...cutsMm);
	const candidates = stockLengthsMm
		.toSorted((a, b) => a - b)
		.filter((length) => length >= maxCut);
	if (candidates.length === 0) {
		throw new Error(
			`Snitt på ${maxCut}mm är för långt för tillgängliga brädor!`,
		);
	}
	return candidates;
}

function packOntoStockLength(
	sortedCutsMm: number[],
	stockLengthMm: number,
	sawKerfMm: number,
): CutBoardPlan[] {
	const boards: CutBoardPlan[] = [];
	for (const cut of sortedCutsMm) {
		let placed = false;
		for (const board of boards) {
			if (board.remainingMm >= cut + sawKerfMm) {
				board.cutsMm.push(cut);
				board.remainingMm -= cut + sawKerfMm;
				placed = true;
				break;
			}
		}

		if (!placed) {
			boards.push({ cutsMm: [cut], remainingMm: stockLengthMm - cut });
		}
	}
	return boards;
}

export function optimizeLumberCutsFFD(
	requests: CutOptimizationRequest[],
	sawKerfMm = 3,
): CutOptimizationByDimension[] {
	return requests.map((request) => {
		if (request.cutsMm.length === 0) {
			const stockLengthMm = request.stockLengthsMm[0] ?? 4800;
			return {
				dimension: request.dimension,
				stockLengthMm,
				totalBoardsToBuy: 0,
				wastePercentage: "0.0%",
				cutPlan: [],
			};
		}

		// Pack onto every trade length that can hold the longest cut and keep
		// the plan that buys the least material. Packing only onto the
		// smallest viable length told users to buy 12×3000 mm (36 m) for
		// twelve 2010 mm studs when 6×4200 mm (25.2 m) does the job.
		const cuts = [...request.cutsMm].sort((a, b) => b - a);
		let stockLengthMm = 0;
		let boards: CutBoardPlan[] = [];
		let bestPurchasedMm = Number.POSITIVE_INFINITY;
		for (const candidate of getCandidateStockLengths(
			request.cutsMm,
			request.stockLengthsMm,
		)) {
			const candidateBoards = packOntoStockLength(cuts, candidate, sawKerfMm);
			const purchasedMm = candidateBoards.length * candidate;
			if (purchasedMm < bestPurchasedMm) {
				bestPurchasedMm = purchasedMm;
				stockLengthMm = candidate;
				boards = candidateBoards;
			}
		}

		const totalRemaining = boards.reduce(
			(acc, board) => acc + board.remainingMm,
			0,
		);
		const denominator = boards.length * stockLengthMm;
		const waste = denominator === 0 ? 0 : (totalRemaining / denominator) * 100;

		return {
			dimension: request.dimension,
			stockLengthMm,
			totalBoardsToBuy: boards.length,
			wastePercentage: `${waste.toFixed(1)}%`,
			cutPlan: boards,
		};
	});
}
